import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import { TranslateService } from '@ngx-translate/core';
import { ProcedimientosService } from 'src/app/_services/procedimientos.service';
import { ServicioGeneral } from 'src/app/_services/servicio-general.service';
import { NotificationService } from 'src/app/_services/notification.service';
import { ConfirmUpdateDialogComponent } from 'src/app/modal/confirmar-update/confirm-update-dialog/confirm-update-dialog.component';
import { ProcedimientoDinamico, Departamento } from 'src/app/model/procedimiendo-dinamico';
import { environment } from 'src/environments/environment';

export interface FormProcedimientoData {
  procedimiento: ProcedimientoDinamico | null;
  isEdit: boolean;
}

@Component({
  selector: 'app-form-procedimiento',
  templateUrl: './form-procedimiento.component.html',
  styleUrls: ['./form-procedimiento.component.scss']
})
export class FormProcedimientoComponent implements OnInit {

  departamentos: Departamento[] = [];
  isEdit: boolean = false;
  procedimiento: ProcedimientoDinamico | null = null;
  readonly ocultarVisible = environment.environment === 'gmu';

  fechaAperturaIncompletaWarning: boolean = false;
  fechaCierreIncompletaWarning: boolean = false;

  formProcedimiento: UntypedFormGroup = new UntypedFormGroup({
    nombre: new UntypedFormControl('', [Validators.required]),
    codigo: new UntypedFormControl('', [Validators.required]),
    sia: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]{1,9}$')]),
    identificador: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]{1,9}$')]),
    visible: new UntypedFormControl(false),
    link_ayuda: new UntypedFormControl(''),
    link_ordenanza: new UntypedFormControl(''),
    link_path: new UntypedFormControl(''),
    departamentoId: new UntypedFormControl(null, [Validators.required]),
    fechaApertura: new UntypedFormControl(''),
    horaApertura: new UntypedFormControl(''),
    fechaCierre: new UntypedFormControl(''),
    horaCierre: new UntypedFormControl(''),
    custom: new UntypedFormControl(false)
  });

  constructor(
    public dialogRef: MatDialogRef<FormProcedimientoComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FormProcedimientoData,
    public dialog: MatDialog,
    private procedimientosService: ProcedimientosService,
    private servicioGeneral: ServicioGeneral,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {
    this.isEdit = data.isEdit;
    this.procedimiento = data.procedimiento;
  }

  ngOnInit(): void {
    this.loadDepartamentos();
  }

  loadDepartamentos(): void {
    this.servicioGeneral.getDepartamentos().subscribe({
      next: (data) => {
        this.departamentos = this.normalizeDepartamentos(data?.departamentos);
        if (this.procedimiento) {
          this.populateForm();
        }
      },
      error: (err) => {
        console.error('Error al cargar departamentos', err);
      }
    });
  }

  populateForm(): void {
    if (!this.procedimiento) return;

    this.formProcedimiento.patchValue({
      nombre: this.procedimiento.nombre || '',
      codigo: this.procedimiento.codigo || '',
      sia: this.procedimiento.sia || '',
      identificador: this.procedimiento.identificador || '',
      visible: this.procedimiento.visible || false,
      link_ayuda: this.procedimiento.link_ayuda || '',
      link_ordenanza: this.procedimiento.link_ordenanza || '',
      link_path: this.procedimiento.link_path || '',
      departamentoId: this.procedimiento.departamento?.id ?? null,
      custom: this.procedimiento.custom || false

    });

    // Cargar fechas si existen
    if (this.procedimiento.fechaApertura) {
      const fechaApertura = this.parseFechaArray(this.procedimiento.fechaApertura);
      if (fechaApertura) {
        this.formProcedimiento.patchValue({
          fechaApertura: fechaApertura.fecha,
          horaApertura: fechaApertura.hora
        });
      }
    }

    if (this.procedimiento.fechaCierre) {
      const fechaCierre = this.parseFechaArray(this.procedimiento.fechaCierre);
      if (fechaCierre) {
        this.formProcedimiento.patchValue({
          fechaCierre: fechaCierre.fecha,
          horaCierre: fechaCierre.hora
        });
      }
    }
  }

  parseFechaArray(fecha: any): { fecha: string, hora: string } | null {
    if (Array.isArray(fecha) && fecha.length >= 5) {
      const year = fecha[0];
      const month = String(fecha[1]).padStart(2, '0');
      const day = String(fecha[2]).padStart(2, '0');
      const hour = String(fecha[3]).padStart(2, '0');
      const minute = String(fecha[4]).padStart(2, '0');
      return {
        fecha: `${year}-${month}-${day}`,
        hora: `${hour}:${minute}`
      };
    }
    return null;
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  openConfirmDialog(): void {
    if (!this.validarFormulario()) {
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;
    dialogConfig.width = '25vw';
    dialogConfig.minWidth = '500px';
    dialogConfig.data = { titulo: this.isEdit ? 'actualización' : 'alta' };

    const dialogRef = this.dialog.open(ConfirmUpdateDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result?.result === 'Confirmar') {
        this.guardarProcedimiento();
      }
    });
  }

  validarFormulario(): boolean {
    this.fechaAperturaIncompletaWarning = false;
    this.fechaCierreIncompletaWarning = false;

    // Marcar todos los campos como touched para mostrar errores de validación
    this.formProcedimiento.markAllAsTouched();

    const fechaApertura = this.formProcedimiento.value.fechaApertura;
    const horaApertura = this.formProcedimiento.value.horaApertura;
    const fechaCierre = this.formProcedimiento.value.fechaCierre;
    const horaCierre = this.formProcedimiento.value.horaCierre;

    // Validar que si se completa fecha también se complete hora y viceversa
    if ((fechaApertura && !horaApertura) || (!fechaApertura && horaApertura)) {
      this.fechaAperturaIncompletaWarning = true;
    }

    if ((fechaCierre && !horaCierre) || (!fechaCierre && horaCierre)) {
      this.fechaCierreIncompletaWarning = true;
    }

    return !this.fechaAperturaIncompletaWarning && !this.fechaCierreIncompletaWarning && this.formProcedimiento.valid;
  }

  guardarProcedimiento(): void {
    const formValue = this.formProcedimiento.value;

    const departamentoSeleccionado = this.departamentos.find(d => d.id === Number(formValue.departamentoId));

    const procedimientoData: ProcedimientoDinamico = {
      nombre: formValue.nombre,
      codigo: formValue.codigo,
      sia: Number(formValue.sia),
      identificador: Number(formValue.identificador),
      visible: this.ocultarVisible ? false : formValue.visible,
      link_ayuda: formValue.link_ayuda || null,
      link_ordenanza: formValue.link_ordenanza || null,
      link_path: formValue.link_path || null,
      departamento: departamentoSeleccionado,
      custom: this.isEdit && this.procedimiento ? !!this.procedimiento.custom : false,
      pestanias: this.isEdit && this.procedimiento ? (this.procedimiento.pestanias ?? []) : []
    };

    // Procesar fechas
    if (formValue.fechaApertura && formValue.horaApertura) {
      procedimientoData.fechaApertura = `${formValue.fechaApertura}T${formValue.horaApertura}:00`;
    }

    if (formValue.fechaCierre && formValue.horaCierre) {
      procedimientoData.fechaCierre = `${formValue.fechaCierre}T${formValue.horaCierre}:00`;
    }

    if (this.isEdit && this.procedimiento?.id) {
      procedimientoData.id = this.procedimiento.id;
    }

    this.procedimientosService.guardaProcedimiento(procedimientoData).subscribe({
      next: () => {
        const mensaje = this.isEdit
          ? this.translate.instant('PROCEDIMIENTOS.MSG.PROC_UPDATED')
          : this.translate.instant('PROCEDIMIENTOS.MSG.PROC_CREATED');
        this.notificationService.success(this.translate.instant('COMMON.SAVED'), mensaje, 5000);
        this.dialogRef.close({ success: true });
      },
      error: (err) => {
        console.error('Error al guardar procedimiento', err);
        const mensaje = this.isEdit
          ? this.translate.instant('PROCEDIMIENTOS.MSG.PROC_UPDATE_FAIL')
          : this.translate.instant('PROCEDIMIENTOS.MSG.PROC_CREATE_FAIL');
        this.notificationService.error(this.translate.instant('COMMON.ERROR'), mensaje, 5000);
      }
    });
  }

  getTitle(): string {
    return this.translate.instant(this.isEdit ? 'PROCEDIMIENTOS.FORM.EDIT' : 'PROCEDIMIENTOS.FORM.NEW');
  }

  /** Acepta id/nombre en minúsculas o PascalCase y descarta filas sin nombre. */
  private normalizeDepartamentos(raw: unknown): Departamento[] {
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .map((item: any) => ({
        id: item?.id ?? item?.Id,
        nombre: String(item?.nombre ?? item?.Nombre ?? '').trim(),
        departamentos: item?.departamentos ?? []
      }))
      .filter((dep) => dep.id != null && dep.nombre.length > 0);
  }
}
